import React, { useCallback, useMemo } from 'react';
import { EntityForm } from '../form.types';
import {
  useQueryGetKiosk,
  useQueryGetKioskSettings,
  useMutationUpdateKioskSettings
} from '../../../lockers/kiosks.queries';
import { KioskThemeSettingsForm } from './form';
import { KioskThemeSettingsFormOutput } from './form-output';
import { kioskThemeSettingsFormSchema } from './form-schema';
import { kioskThemeSettingsFormUISchema } from './ui-schema';
import { KioskSettingsFormOutput } from '../kiosk-form/form-output';
import { KioskThemePreview, KioskThemePreviewButtonLabels } from './kiosk-theme-preview.component';
import { getDefaultKioskBackgroundUrl, isDefaultKioskBackgroundUrl } from '../shared/default-kiosk-backgrounds';

export function useEditKioskThemeForm(
  kioskID: string | undefined,
  projectID: string | undefined
): EntityForm<KioskThemeSettingsForm, KioskThemeSettingsFormOutput> | null {
  const settingsResult = useQueryGetKioskSettings(kioskID);
  const kioskResult = useQueryGetKiosk(kioskID);
  const updateMutation = useMutationUpdateKioskSettings();

  const previewTitle = useMemo(() => {
    const kiosk = kioskResult.data;
    if (!kiosk) return undefined;
    return kiosk.name ? `${kiosk.hubName} - ${kiosk.name}` : kiosk.hubName;
  }, [kioskResult.data]);

  const defaultValues = useMemo((): KioskThemeSettingsForm => {
    if (!settingsResult.data) return {} as KioskThemeSettingsForm;
    return KioskThemeSettingsFormOutput.FromDto(settingsResult.data);
  }, [settingsResult.data]);

  const initialFormOutput = useMemo(() => {
    return new KioskThemeSettingsFormOutput(defaultValues);
  }, [defaultValues]);

  const onSubmit = useCallback(
    async (formData: Record<string, any>) => {
      if (!kioskID) return;
      await updateMutation.mutateAsync({
        kioskID,
        dto: KioskThemeSettingsFormOutput.ToDto(formData as KioskThemeSettingsFormOutput),
        projectID
      });
    },
    [kioskID, projectID, updateMutation]
  );

  const buttonLabels = useMemo((): KioskThemePreviewButtonLabels => {
    if (!settingsResult.data) {
      return { showInternal: true, internalLabel: 'Residents', showExternal: true, externalLabel: 'Couriers' };
    }
    const kioskForm = KioskSettingsFormOutput.FromDto(settingsResult.data);
    return {
      showInternal: kioskForm.showInternalUserButton === 'yes',
      internalLabel: kioskForm.internalUserButtonText || 'Residents',
      showExternal: kioskForm.showExternalUserButton === 'yes',
      externalLabel: kioskForm.externalUserButtonText || 'Couriers'
    };
  }, [settingsResult.data]);

  const componentToRenderBelowForm = useCallback(
    (props: { formData: Record<string, any> }) =>
      React.createElement(KioskThemePreview, { formData: props.formData, buttonLabels, title: previewTitle }),
    [buttonLabels, previewTitle]
  );

  const onChangeInterceptor = useCallback(
    (currentFormData: KioskThemeSettingsForm, newFormData: KioskThemeSettingsForm): KioskThemeSettingsForm => {
      let next = newFormData;
      if (
        newFormData?.themeHome !== currentFormData?.themeHome &&
        isDefaultKioskBackgroundUrl(currentFormData?.backgroundHome)
      ) {
        next = { ...next, backgroundHome: getDefaultKioskBackgroundUrl(newFormData.themeHome) };
      }
      if (
        newFormData?.themeOtherScreens !== currentFormData?.themeOtherScreens &&
        isDefaultKioskBackgroundUrl(currentFormData?.backgroundSession)
      ) {
        next = { ...next, backgroundSession: getDefaultKioskBackgroundUrl(newFormData.themeOtherScreens) };
      }
      return next;
    },
    []
  );

  if (!kioskID) return null;

  return {
    isLoading: settingsResult.isLoading,
    title: 'Theme',
    subtitle: kioskID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema: kioskThemeSettingsFormSchema,
    uiSchema: kioskThemeSettingsFormUISchema,
    getOutput: (formData) => new KioskThemeSettingsFormOutput(formData as KioskThemeSettingsForm),
    onChangeInterceptor,
    onSubmit,
    componentToRenderBelowForm,
    isLocked: false,
    lockedMessage: null
  };
}
